import { useState, useEffect, useRef } from 'react';
import { reportApi } from '../../api/report.api';
import { useUI } from '../../store/UIContext';
import { useAuth } from '../../store/AuthContext';
import { PageHeader, Btn, Spinner, Select } from '../../components/ui/index';
import { EmojiBar } from '../../components/ui/EmojiPicker';
import EstimatedTimeSlider, { DEFAULT_ESTIMATED_TIME } from '../../components/ui/EstimatedTimeSlider';
import DailyReportPrintForm from '../../components/reports/DailyReportPrintForm';
import useUserSignature from '../../hooks/useUserSignature';
import { serializeDailyReportItem, parseDailyReportItemContent, invoiceCostLabel } from '../../utils/dailyReportItemCodec';
import { companyApi } from '../../api/company.api';
import { timeAgo, formatPersianDateLong } from '../../utils/persianNumber';
import { BASE_URL, BASE_MEDIA_URL } from '../../api/client';
import { getDailyReportApprovalDisplay, normalizeDailyReportApproval } from '../../utils/dailyReportApproval';
import { ClipboardText, Robot } from '@phosphor-icons/react';
import { useCompanyDisplay } from '../../utils/companyDisplay';

const GUEST_COMPANY = 'مهمان';


const BUILTIN_TICKETS_MISSIONS_ID = 'builtin-tickets-missions';
const BUILTIN_OTHER_ID = 'builtin-other';
const BUILTIN_TICKETS_MISSIONS = {
  item_id: BUILTIN_TICKETS_MISSIONS_ID,
  name: 'تیکت‌ها و ماموریت',
  need_cost: false,
  need_company_name: false,
  need_phone_number: false,
  need_full_name: false,
};
const BUILTIN_OTHER = {
  item_id: BUILTIN_OTHER_ID,
  name: 'سایر',
  need_cost: false,
  need_company_name: false,
  need_phone_number: false,
  need_full_name: false,
};

export default function DailyReportPage() {
  const { addToast } = useUI();
  const { user } = useAuth();
  const { signatureUrl } = useUserSignature();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [addTitle, setAddTitle] = useState('');
  const [addDesc, setAddDesc] = useState('');
  const addDescRef = useRef(null);
  const [addCompany, setAddCompany] = useState('');
  const [addPhoneNumber, setAddPhoneNumber] = useState('');
  const [addFullName, setAddFullName] = useState('');
  const [addEstimatedTime, setAddEstimatedTime] = useState(DEFAULT_ESTIMATED_TIME);
  const [companyOptions, setCompanyOptions] = useState([]);
  const companyLabel = useCompanyDisplay();
  const [deptParts, setDeptParts] = useState([]);
  const [activePart, setActivePart] = useState(BUILTIN_TICKETS_MISSIONS);
  const [addingItem, setAddingItem] = useState(false);
  const [draftSaving, setDraftSaving] = useState(false);
  const [reportRouting, setReportRouting] = useState({});
  const [approvalStatus, setApprovalStatus] = useState(null);
  const draftTimerRef = useRef(null);

  // Tickets/missions this employee actually did, picked to attach to the
  // report - the AI-facing "respond to these" step comes later; for now
  // this just surfaces and persists the selection (see
  // get_employee_done_items / save_daily_report_selected_items).
  const [doneTickets, setDoneTickets] = useState([]);
  const [doneMissions, setDoneMissions] = useState([]);
  const [doneFilter, setDoneFilter] = useState('all'); // all | ticket | mission
  const [doneLoading, setDoneLoading] = useState(true);

  // Streamed Persian summary from selected tickets/missions
  // (summarize_daily_report). Copied into the add form — never creates
  // freeform items without a manager-defined part.
  const [smartReport, setSmartReport] = useState('');
  const [smartReportStreaming, setSmartReportStreaming] = useState(false);
  const esRef = useRef(null);
  const draftReadyRef = useRef(false);

  useEffect(() => () => esRef.current?.close(), []);

  const applyDailyReportPayload = (payload) => {
    const textData = payload?.text_data || (Array.isArray(payload) ? payload : []);
    const restored = textData.map(row => {
      const parsed = parseDailyReportItemContent(row.content || '');
      return {
        id: row.report_id,
        title: parsed.title,
        description: parsed.description,
        companyName: parsed.companyName,
        phoneNumber: parsed.phoneNumber,
        fullName: parsed.fullName,
        category: parsed.category,
        partId: parsed.partId,
        invoiceId: parsed.invoiceId,
        invoiceStatus: parsed.invoiceStatus,
        invoiceStatusLabel: parsed.invoiceStatusLabel,
        invoiceDepartmentName: parsed.invoiceDepartmentName,
        estimatedTime: parsed.estimatedTime,
      };
    });
    setItems(restored);
    if (payload && !Array.isArray(payload)) {
      setApprovalStatus(normalizeDailyReportApproval(payload));
    }
  };

  const refreshApprovalStatus = () => {
    reportApi.getDailyReport()
      .then(r => {
        const payload = r.data;
        if (payload && !Array.isArray(payload)) {
          setApprovalStatus(normalizeDailyReportApproval(payload));
        }
      })
      .catch(() => {});
  };

  useEffect(() => {
    reportApi.getDailyReport()
      .then(r => applyDailyReportPayload(r.data))
      .catch(()=>addToast('خطا در بارگذاری پیش‌نویس گزارش', 'error'))
      .finally(() => { setLoading(false); draftReadyRef.current = true; });
    reportApi.getEmployeeDoneItems()
      .then(r => {
        setDoneTickets((r.data?.tickets || []).map(t => ({ ...t, selected: t.selected !== false })));
        setDoneMissions((r.data?.missions || []).map(m => ({ ...m, selected: m.selected !== false })));
      })
      .catch(() => {})
      .finally(() => setDoneLoading(false));
    reportApi.getDailyReportItems()
      .then(r => setDeptParts(Array.isArray(r.data) ? r.data : []))
      .catch(() => {});
    companyApi.getCompanyNameList()
      .then(r => {
        const names = Array.isArray(r.data) ? r.data.filter(Boolean) : [];
        setCompanyOptions([GUEST_COMPANY, ...names]);
      })
      .catch(() => setCompanyOptions([GUEST_COMPANY]));
    reportApi.getDailyReportRouting()
      .then(r => setReportRouting(r.data && typeof r.data === 'object' ? r.data : {}))
      .catch(() => {});
  }, []);

  const partIdMatches = (a, b) => a != null && b != null && String(a) === String(b);
  const selectableParts = () => {
    const fromApi = Array.isArray(deptParts) ? deptParts : [];
    const built = [BUILTIN_TICKETS_MISSIONS, BUILTIN_OTHER];
    const ids = new Set(fromApi.map((p) => String(p.item_id)));
    return [...built.filter((p) => !ids.has(String(p.item_id))), ...fromApi];
  };
  const partForSmartCopy = () => activePart || BUILTIN_TICKETS_MISSIONS;

  const needsCompany = !!(activePart?.need_company_name || activePart?.need_cost);
  const needsPhoneNumber = !!activePart?.need_phone_number;
  const needsFullName = !!activePart?.need_full_name;
  const needsFinanceInvoice = !!activePart?.need_cost;
  const financeRouteDept = reportRouting.finance_invoice?.department_name;

  useEffect(() => {
    if (!draftReadyRef.current || loading) return undefined;
    if (draftTimerRef.current) clearTimeout(draftTimerRef.current);
    draftTimerRef.current = setTimeout(() => {
      setDraftSaving(true);
      persistItemsToDraft(items)
        .catch(() => {})
        .finally(() => {
          setDraftSaving(false);
          refreshApprovalStatus();
        });
    }, 1500);
    return () => { if (draftTimerRef.current) clearTimeout(draftTimerRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, loading]);

  const toggleDoneTicket = (id) => setDoneTickets(prev => prev.map(t => t.id === id ? { ...t, selected: !t.selected } : t));
  const toggleDoneMission = (id) => setDoneMissions(prev => prev.map(m => m.id === id ? { ...m, selected: !m.selected } : m));

  const generateSmartReport = () => {
    const ticketIds = doneTickets.filter(t => t.selected).map(t => t.id);
    const missionIds = doneMissions.filter(m => m.selected).map(m => m.id);
    if (ticketIds.length === 0 && missionIds.length === 0) {
      addToast('حداقل یک تیکت یا ماموریت انتخاب کنید', 'error');
      return;
    }
    esRef.current?.close();
    setSmartReport('');
    setSmartReportStreaming(true);

    const qs = [
      ...ticketIds.map(id => `ticket_ids[]=${encodeURIComponent(id)}`),
      ...missionIds.map(id => `mission_ids[]=${encodeURIComponent(id)}`),
    ].join('&');
    const es = new EventSource(`${BASE_URL}summarize_daily_report?${qs}`, { withCredentials: true });
    esRef.current = es;

    es.onmessage = (e) => {
      if (e.data === '[DONE]') { es.close(); setSmartReportStreaming(false); return; }
      try {
        const event = JSON.parse(e.data);
        if (event.type === 'chunk') setSmartReport(prev => prev + event.content);
        else if (event.type === 'error') addToast('خطا در تولید گزارش هوشمند', 'error');
      } catch {}
    };
    es.onerror = () => {
      es.close();
      setSmartReportStreaming(false);
    };
  };

  // Copy streamed AI text into the items list under the selected
  // manager-defined part — never creates freeform rows without partId.
  const copySmartReportToList = async () => {
    if (!activePart) {
      addToast('ابتدا یک بخش تعریف‌شده توسط مدیر را انتخاب کنید', 'error');
      return;
    }
    if (!smartReport.trim()) {
      addToast('متن گزارش هوشمند خالی است', 'error');
      return;
    }
    if (needsCompany && !addCompany) {
      addToast('لطفاً شرکت را از لیست انتخاب کنید', 'error');
      return;
    }
    if (needsPhoneNumber && !addPhoneNumber.trim()) {
      addToast('لطفاً شماره تماس را وارد کنید', 'error');
      return;
    }
    if (needsFullName && !addFullName.trim()) {
      addToast('لطفاً نام و نام خانوادگی را وارد کنید', 'error');
      return;
    }
    if (needsFinanceInvoice && !reportRouting.finance_invoice?.department_id) {
      addToast('دپارتمان مقصد فاکتور مالی هنوز توسط دیسپچر تعیین نشده — لطفاً با دیسپچر هماهنگ کنید', 'error');
      return;
    }

    setAddingItem(true);
    const part = partForSmartCopy();
    let invoiceId = null;
    let invoiceStatus = null;
    let invoiceStatusLabel = null;
    let invoiceDepartmentName = null;
    const title = (part.name || '\u06AF\u0632\u0627\u0631\u0634 \u0631\u0648\u0632\u0627\u0646\u0647 \u0647\u0648\u0634\u0645\u0646\u062F').trim();
    const description = smartReport.trim();
    try {
      if (needsFinanceInvoice) {
        const res = await reportApi.createDailyReportInvoice({
          company_name: addCompany,
          item_title: title,
          item_description: description,
          part_id: part.item_id,
        });
        invoiceId = res.data?.invoice_id;
        invoiceStatus = res.data?.status;
        invoiceStatusLabel = res.data?.status_label;
        invoiceDepartmentName = res.data?.department_name || financeRouteDept;
        addToast(
          invoiceId ? `فاکتور #${invoiceId} ثبت شد` : 'فاکتور ثبت شد',
          'success',
        );
      }

      const newItem = {
        id: Date.now(),
        title,
        description,
        companyName: needsCompany ? addCompany : '',
        phoneNumber: needsPhoneNumber ? addPhoneNumber.trim() : '',
        fullName: needsFullName ? addFullName.trim() : '',
        category: 'other',
        estimatedTime: addEstimatedTime,
        partId: part.item_id,
        invoiceId,
        invoiceStatus,
        invoiceStatusLabel,
        invoiceDepartmentName,
      };
      setItems(prev => [...prev, newItem]);
      setAddTitle('');
      setAddDesc('');
      setAddCompany('');
      setAddPhoneNumber('');
      setAddFullName('');
      setAddEstimatedTime(DEFAULT_ESTIMATED_TIME);
      setActivePart(BUILTIN_TICKETS_MISSIONS);
      addToast('گزارش هوشمند به لیست قسمت‌ها اضافه شد', 'success');
    } catch (err) {
      const msg = err.response?.data;
      addToast(typeof msg === 'string' && msg ? msg : 'خطا در افزودن گزارش هوشمند به لیست', 'error');
    }
    setAddingItem(false);
  };

  // Clears previous draft rows, then recreates one draft row per item.
  const persistItemsToDraft = async (itemsToSave) => {
    try {
      const existing = await reportApi.getDailyReport();
      const rows = Array.isArray(existing.data) ? existing.data : existing.data?.text_data || [];
      for (const row of rows) {
        if (row.report_id) await reportApi.removeDailyReport({ report_id: row.report_id });
      }
    } catch {}
    for (const it of itemsToSave) {
      const content = serializeDailyReportItem(it);
      const created = await reportApi.addEmptyDailyReport();
      const reportId = created?.data?.report_id;
      if (reportId) await reportApi.updateDailyReport({ report_id: reportId, content });
    }
  };

  const addItem = async () => {
    if (!activePart) {
      addToast('یک بخش تعریف‌شده توسط مدیر را انتخاب کنید', 'error');
      return;
    }
    if (!addTitle.trim()) {
      addToast('عنوان مورد الزامی است', 'error');
      return;
    }
    if (needsCompany && !addCompany) {
      addToast('لطفاً شرکت را از لیست انتخاب کنید', 'error');
      return;
    }

    if (needsPhoneNumber && !addPhoneNumber.trim()) {
      addToast('لطفاً شماره تماس را وارد کنید', 'error');
      return;
    }
    if (needsFullName && !addFullName.trim()) {
      addToast('لطفاً نام و نام خانوادگی را وارد کنید', 'error');
      return;
    }
    if (needsFinanceInvoice && !reportRouting.finance_invoice?.department_id) {
      addToast('دپارتمان مقصد فاکتور مالی هنوز توسط دیسپچر تعیین نشده — لطفاً با دیسپچر هماهنگ کنید', 'error');
      return;
    }

    setAddingItem(true);
    let invoiceId = null;
    let invoiceStatus = null;
    let invoiceStatusLabel = null;
    let invoiceDepartmentName = null;
    try {
      if (needsFinanceInvoice) {
        const res = await reportApi.createDailyReportInvoice({
          company_name: addCompany,
          item_title: addTitle.trim(),
          item_description: addDesc.trim(),
          part_id: activePart.item_id,
        });
        invoiceId = res.data?.invoice_id;
        invoiceStatus = res.data?.status;
        invoiceStatusLabel = res.data?.status_label;
        invoiceDepartmentName = res.data?.department_name || financeRouteDept;
        addToast(
          invoiceId ? `فاکتور #${invoiceId} ثبت شد` : 'فاکتور ثبت شد',
          'success',
        );
      }

      const newItem = {
        id: Date.now(),
        title: addTitle.trim(),
        description: addDesc.trim(),
        companyName: needsCompany ? addCompany : '',
        phoneNumber: needsPhoneNumber ? addPhoneNumber.trim() : '',
        fullName: needsFullName ? addFullName.trim() : '',
        // Category is no longer employee-picked; keep a stable default for
        // older print/codec paths that still expect the field.
        category: 'other',
        estimatedTime: addEstimatedTime,
        partId: activePart.item_id,
        invoiceId,
        invoiceStatus,
        invoiceStatusLabel,
        invoiceDepartmentName,
      };
      setItems(prev => [...prev, newItem]);
      setAddTitle('');
      setAddDesc('');
      setAddCompany('');
      setAddPhoneNumber('');
      setAddFullName('');
      setAddEstimatedTime(DEFAULT_ESTIMATED_TIME);
      setActivePart(BUILTIN_TICKETS_MISSIONS);
    } catch (err) {
      const msg = err.response?.data;
      addToast(typeof msg === 'string' && msg ? msg : 'خطا در ثبت مورد گزارش', 'error');
    }
    setAddingItem(false);
  };

  const applyDeptPart = (part) => {
    setActivePart(part);
    setAddTitle(part.name);
    setAddDesc('');
    setAddCompany('');
    setAddPhoneNumber('');
    setAddFullName('');
    setAddEstimatedTime(DEFAULT_ESTIMATED_TIME);
  };

  const removeItem = id => setItems(prev => prev.filter(it => it.id !== id));
  const updateItemEstimatedTime = (id, estimatedTime) => {
    setItems(prev => prev.map(it => (it.id === id ? { ...it, estimatedTime } : it)));
  };




  // Runs the three save steps (draft persist -> finalize -> selected
  // items) as separate try/catches so a failure names the actual step
  // that broke instead of one generic "save failed" message, and stops
  // at the first failing step rather than pressing on with a half-saved
  // report.
  const saveReportFlow = async (itemsToSave) => {
    try {
      await persistItemsToDraft(itemsToSave);
    } catch {
      addToast('خطا در ذخیره پیش‌نویس موارد گزارش', 'error');
      return;
    }
    try {
      await reportApi.saveDailyReport();
    } catch {
      addToast('خطا در ثبت نهایی گزارش', 'error');
      return;
    }
    try {
      await reportApi.saveDailyReportSelectedItems({
        'ticket_ids[]': doneTickets.filter(t => t.selected).map(t => t.id),
        'mission_ids[]': doneMissions.filter(m => m.selected).map(m => m.id),
      });
    } catch {
      addToast('گزارش ذخیره شد اما ثبت تیکت‌ها و ماموریت‌های انتخاب‌شده ناموفق بود', 'error');
      return;
    }
    addToast('گزارش با موفقیت ذخیره شد', 'success');
    refreshApprovalStatus();
  };

  const save = async () => {
    setSaving(true);
    await saveReportFlow(items);
    setSaving(false);
  };

  const partLabel = (partId) => {
    if (partId == null) return '—';
    if (String(partId) === BUILTIN_TICKETS_MISSIONS_ID) return BUILTIN_TICKETS_MISSIONS.name;
    if (String(partId) === BUILTIN_OTHER_ID) return BUILTIN_OTHER.name;
    const part = deptParts.find(p => p.item_id === partId || String(p.item_id) === String(partId));
    return part?.name || '—';
  };

  const reportDateLabel = formatPersianDateLong(new Date());
  const employeeName = [user?.first_name, user?.last_name].filter(Boolean).join(' ') || user?.username || '';
  const departmentName = user?.department_name || '';

  const handlePrint = () => {
    window.print();
  };

  const approvalDisplay = getDailyReportApprovalDisplay(approvalStatus);
  const managerSignatureUrl = approvalStatus?.manager_signature_file
    ? `${BASE_MEDIA_URL}${approvalStatus.manager_signature_file}`
    : null;

  return (
    <div className="fade-in" style={{ minWidth: 0, maxWidth: '100%', overflow: 'hidden' }}>
      <DailyReportPrintForm
        dateLabel={reportDateLabel}
        employeeName={employeeName}
        departmentName={departmentName}
        items={items}
        parts={selectableParts()}
        partLabel={partLabel}
        expertSignatureUrl={signatureUrl}
        managerSignatureUrl={managerSignatureUrl}
        managerName={approvalStatus?.approved_by_name}
        approvalStatusLabel={approvalDisplay?.label}
        approvedAt={approvalStatus?.approved_at}
      />
      <PageHeader title="گزارش روزانه" subtitle={formatPersianDateLong(new Date())}
        action={
          <div style={{display:'flex',gap:8,flexWrap:'wrap',justifyContent:'flex-end'}}>
            <Btn variant="secondary" onClick={handlePrint}>🖨️ چاپ فرم</Btn>
            <Btn onClick={save} loading={saving}>ذخیره گزارش</Btn>
          </div>
        }
      />
      {approvalDisplay && (
        <div style={{
          background: approvalDisplay.bg,
          border: `2px solid ${approvalDisplay.border}`,
          borderRadius: 12,
          padding: '14px 18px',
          marginBottom: 16,
          display: 'flex',
          alignItems: 'flex-start',
          gap: 12,
        }}>
          <span style={{
            fontSize: 20,
            lineHeight: 1,
            width: 36,
            height: 36,
            borderRadius: '50%',
            background: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            border: `1px solid ${approvalDisplay.border}`,
          }}>
            {approvalDisplay.icon}
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: approvalDisplay.color, marginBottom: 4 }}>
              وضعیت گزارش: {approvalDisplay.label}
            </div>
            <div style={{ fontSize: 13, color: approvalDisplay.color, lineHeight: 1.6 }}>
              {approvalDisplay.detail}
            </div>
          </div>
        </div>
      )}
      {(financeRouteDept || deptParts.some(p => p.need_cost)) && (
        <div style={{
          background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10,
          padding: '8px 14px', marginBottom: 16, fontSize: 12, color: '#166534',
        }}>
          هزینه‌ها به‌صورت فاکتور ثبت می‌شوند —{' '}
          <a href="/reports/invoices" style={{ color: '#166534', fontWeight: 600 }}>لیست فاکتورها</a>
        </div>
      )}
      <div className="pj-daily-report-grid">
        <div style={{display:'flex',flexDirection:'column',gap:16}}>
          {/* Tickets/missions done - default: all selected */}
          <div style={{background:'#fff',borderRadius:14,border:'1px solid rgba(35,58,122,0.1)',padding:20}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:14,flexWrap:'wrap',gap:8}}>
              <h3 style={{fontSize:14,fontWeight:700,color:'#0f172a',margin:0}}>تیکت‌ها و ماموریت‌های انجام‌شده</h3>
              <div style={{display:'flex',gap:6}}>
                {[['all','همه'],['ticket','تیکت‌ها'],['mission','ماموریت‌ها']].map(([id,label])=>(
                  <button key={id} onClick={()=>setDoneFilter(id)} style={{padding:'5px 12px',borderRadius:16,border:`1.5px solid ${doneFilter===id?'#29AAE1':'#e2e8f0'}`,background:doneFilter===id?'#EDF3FC':'#fff',color:doneFilter===id?'#233A7A':'#64748b',fontSize:11,fontWeight:600,cursor:'pointer',fontFamily:'inherit'}}>{label}</button>
                ))}
              </div>
            </div>
            {doneLoading ? <Spinner/> : (doneTickets.length===0 && doneMissions.length===0) ? (
              <p style={{fontSize:12,color:'#94a3b8',textAlign:'center',padding:'10px 0'}}>موردی برای این کارمند یافت نشد</p>
            ) : (
              <div style={{display:'flex',flexDirection:'column',gap:6}}>
                {doneFilter!=='mission' && doneTickets.map(t=>(
                  <label key={`t${t.id}`} style={{display:'flex',alignItems:'center',gap:8,padding:'8px 10px',borderRadius:8,background:'#f8faff',cursor:'pointer'}}>
                    <input type="checkbox" checked={t.selected} onChange={()=>toggleDoneTicket(t.id)} />
                    <span style={{fontSize:10,fontWeight:700,color:'#29AAE1',background:'#D9E4F7',padding:'1px 7px',borderRadius:8}}>تیکت</span>
                    <span style={{fontSize:12,color:'#334155',flex:1}}>{t.title}</span>
                    {t.date && <span style={{fontSize:10,color:'#94a3b8'}}>{timeAgo(t.date)}</span>}
                  </label>
                ))}
                {doneFilter!=='ticket' && doneMissions.map(m=>(
                  <label key={`m${m.id}`} style={{display:'flex',alignItems:'center',gap:8,padding:'8px 10px',borderRadius:8,background:'#f8faff',cursor:'pointer'}}>
                    <input type="checkbox" checked={m.selected} onChange={()=>toggleDoneMission(m.id)} />
                    <span style={{fontSize:10,fontWeight:700,color:'#059669',background:'#dcfce7',padding:'1px 7px',borderRadius:8}}>ماموریت</span>
                    <span style={{fontSize:12,color:'#334155',flex:1}}>{m.title}</span>
                    {m.date && <span style={{fontSize:10,color:'#94a3b8'}}>{timeAgo(m.date)}</span>}
                  </label>
                ))}
              </div>
            )}
            <Btn variant="secondary" onClick={generateSmartReport} disabled={smartReportStreaming} style={{width:'100%',justifyContent:'center',marginTop:14}}>
              {smartReportStreaming?<><Spinner size={13} color="currentColor"/> در حال تولید گزارش...</>:<><Robot size={14} weight="regular" /> تولید گزارش روزانه هوشمند</>}
            </Btn>
            {(smartReport || smartReportStreaming) && (
              <div style={{marginTop:14,padding:16,background:'#faf5ff',border:'1px solid rgba(139,92,246,0.2)',borderRadius:10}}>
                <p style={{fontSize:13,lineHeight:1.9,color:'#334155',whiteSpace:'pre-wrap',margin:0}}>
                  {smartReport}{smartReportStreaming && '▋'}
                </p>
                {!smartReportStreaming && smartReport && (
                  <div style={{display:'flex',gap:8,marginTop:14,flexWrap:'wrap',alignItems:'center'}}>
                    <Btn
                      size="sm"
                      onClick={copySmartReportToList}
                      loading={addingItem}
                      disabled={needsFinanceInvoice && !reportRouting.finance_invoice?.department_id}
                    >
                      <ClipboardText size={14} weight="regular" /> کپی به لیست قسمت‌ها
                    </Btn>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Add item — only manager-defined parts + fields those parts require */}
          <div style={{background:'#fff',borderRadius:14,border:'1px solid rgba(35,58,122,0.1)',padding:20,overflow:'hidden'}}>
            <h3 style={{fontSize:14,fontWeight:700,color:'#0f172a',marginBottom:14}}>افزودن مورد جدید</h3>
            {selectableParts().length === 0 ? (
              <p style={{fontSize:13,color:'#94a3b8',textAlign:'center',padding:'18px 0',margin:0}}>
                هنوز بخشی توسط مدیر دپارتمان تعریف نشده است. پس از تعریف بخش‌ها در تنظیمات، اینجا نمایش داده می‌شوند.
              </p>
            ) : (
              <>
                <div style={{marginBottom:14}}>
                  <div style={{fontSize:12,fontWeight:600,color:'#64748b',marginBottom:8}}>بخش‌های تعریف‌شده توسط مدیر</div>
                  <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
                    {selectableParts().map(part => (
                      <button
                        key={part.item_id}
                        type="button"
                        onClick={() => applyDeptPart(part)}
                        style={{
                          padding:'6px 12px', borderRadius:16, cursor:'pointer', fontFamily:'inherit', fontSize:11, fontWeight:600,
                          border:`1.5px solid ${partIdMatches(activePart?.item_id, part.item_id) ? '#29AAE1' : '#e2e8f0'}`,
                          background: partIdMatches(activePart?.item_id, part.item_id) ? '#EDF3FC' : '#fff',
                          color: partIdMatches(activePart?.item_id, part.item_id) ? '#233A7A' : '#64748b',
                        }}
                      >
                        {part.parent_id ? `↳ ${part.name}` : part.name}
                      </button>
                    ))}
                  </div>
                </div>

                {!activePart ? (
                  <p style={{fontSize:12,color:'#94a3b8',margin:'0 0 4px'}}>یک بخش را انتخاب کنید تا فیلدهای مربوط به آن نمایش داده شود.</p>
                ) : (
                  <>
                    <input
                      value={addTitle}
                      onChange={e=>setAddTitle(e.target.value)}
                      placeholder="عنوان مورد..."
                      style={{width:'100%',padding:'9px 12px',border:'1.5px solid #e2e8f0',borderRadius:8,fontSize:13,outline:'none',fontFamily:'inherit',marginBottom:8,boxSizing:'border-box'}}
                    />
                    <textarea
                      ref={addDescRef}
                      value={addDesc}
                      onChange={e=>setAddDesc(e.target.value)}
                      placeholder="توضیحات..."
                      rows={2}
                      style={{width:'100%',padding:'9px 12px',border:'1.5px solid #e2e8f0',borderRadius:8,fontSize:13,outline:'none',fontFamily:'inherit',resize:'vertical',marginBottom:8,boxSizing:'border-box'}}
                    />
                    <div style={{marginBottom:10}}><EmojiBar inputRef={addDescRef} value={addDesc} onInsert={setAddDesc} /></div>

                    {needsCompany && (
                      <div style={{ marginBottom: 10 }}>
                        <Select
                          label="شرکت *"
                          value={addCompany}
                          onChange={(e) => setAddCompany(e.target.value)}
                          options={[
                            { value: '', label: '— انتخاب شرکت —' },
                            ...companyOptions.map((name) => ({ value: name, label: companyLabel(name) })),
                          ]}
                          wrapStyle={{ marginBottom: 0 }}
                        />
                      </div>
                    )}
                    {needsPhoneNumber && (
                      <input
                        value={addPhoneNumber}
                        onChange={e=>setAddPhoneNumber(e.target.value)}
                        placeholder="شماره تماس *"
                        style={{width:'100%',padding:'9px 12px',border:'1.5px solid #e2e8f0',borderRadius:8,fontSize:13,outline:'none',fontFamily:'inherit',marginBottom:8,boxSizing:'border-box'}}
                      />
                    )}
                    {needsFullName && (
                      <input
                        value={addFullName}
                        onChange={e=>setAddFullName(e.target.value)}
                        placeholder="نام و نام خانوادگی *"
                        style={{width:'100%',padding:'9px 12px',border:'1.5px solid #e2e8f0',borderRadius:8,fontSize:13,outline:'none',fontFamily:'inherit',marginBottom:8,boxSizing:'border-box'}}
                      />
                    )}

                    {needsFinanceInvoice && (
                      <p style={{
                        margin: '0 0 10px', padding: '8px 10px', borderRadius: 8, fontSize: 12, lineHeight: 1.6,
                        background: financeRouteDept ? '#fffbeb' : '#fef2f2',
                        border: `1px solid ${financeRouteDept ? '#fde68a' : '#fecaca'}`,
                        color: financeRouteDept ? '#92400e' : '#dc2626',
                      }}>
                        {financeRouteDept
                          ? 'قیمت بعداً توسط مالی ثبت می‌شود.'
                          : 'ثبت هزینه موقتاً غیرفعال است — با دیسپچر هماهنگ کنید.'}
                      </p>
                    )}

                    <EstimatedTimeSlider
                      value={addEstimatedTime}
                      onChange={setAddEstimatedTime}
                      style={{ marginBottom: 10 }}
                    />

                    <Btn
                      size="sm"
                      onClick={addItem}
                      loading={addingItem}
                      disabled={needsFinanceInvoice && !reportRouting.finance_invoice?.department_id}
                    >
                      + افزودن
                    </Btn>
                    {draftSaving && <span style={{ fontSize: 11, color: '#16a34a', marginRight: 10 }}>ذخیره پیش‌نویس...</span>}
                  </>
                )}
              </>
            )}
          </div>

          {/* Items list */}
          {loading?<div style={{background:'#fff',borderRadius:14,border:'1px solid #e2e8f0',padding:20,textAlign:'center'}}><Spinner/></div>
          :items.length===0?<div style={{background:'#fff',borderRadius:14,border:'1px solid #e2e8f0',padding:40,textAlign:'center',color:'#94a3b8',fontSize:13}}>موردی اضافه نشده</div>
          :<div style={{display:'flex',flexDirection:'column',gap:8}}>
              {items.map((it,i)=>(
                <div key={it.id||i} style={{background:'#fff',borderRadius:12,border:'1px solid #e2e8f0',padding:'14px 16px',display:'flex',gap:12,alignItems:'flex-start'}}>
                  <div style={{width:4,height:'100%',minHeight:40,background:'#29AAE1',borderRadius:2,flexShrink:0}}/>
                  <div style={{flex:1}}>
                    <div style={{marginBottom:4,display:'flex',gap:8,alignItems:'center',flexWrap:'wrap'}}>
                      <span style={{fontSize:13,fontWeight:700,color:'#0f172a'}}>{it.title}</span>
                      {it.partId != null && (
                        <span style={{fontSize:10,fontWeight:600,color:'#475569',background:'#f1f5f9',padding:'2px 8px',borderRadius:8}}>
                          {partLabel(it.partId)}
                        </span>
                      )}
                    </div>
                    {it.description&&<p style={{fontSize:12,color:'#64748b',margin:0,lineHeight:1.6}}>{it.description}</p>}
                    {(it.companyName || it.phoneNumber || it.fullName || it.invoiceId) && (
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 6 }}>
                        {it.companyName && (
                          <span style={{ fontSize: 10, color: '#233A7A', background: '#D9E4F7', padding: '2px 8px', borderRadius: 8 }}>
                            شرکت: {companyLabel(it.companyName)}
                          </span>
                        )}
                        {it.phoneNumber && (
                          <span style={{ fontSize: 10, color: '#0f766e', background: '#ccfbf1', padding: '2px 8px', borderRadius: 8 }}>
                            تماس: {it.phoneNumber}
                          </span>
                        )}
                        {it.fullName && (
                          <span style={{ fontSize: 10, color: '#7c3aed', background: '#ede9fe', padding: '2px 8px', borderRadius: 8 }}>
                            نام: {it.fullName}
                          </span>
                        )}
                        {it.invoiceId && (
                          <span style={{ fontSize: 10, color: '#b45309', background: '#fef3c7', padding: '2px 8px', borderRadius: 8 }}>
                            {invoiceCostLabel(it)}
                          </span>
                        )}
                      </div>
                    )}
                    <EstimatedTimeSlider
                      value={it.estimatedTime ?? DEFAULT_ESTIMATED_TIME}
                      onChange={(minutes) => updateItemEstimatedTime(it.id, minutes)}
                      style={{ marginTop: 10 }}
                    />
                  </div>
                  <button onClick={()=>removeItem(it.id||i)} style={{color:'#ef4444',background:'none',border:'none',cursor:'pointer',fontSize:16,lineHeight:1}}>✕</button>
                </div>
              ))}
            </div>
          }
        </div>
      </div>
    </div>
  );
}
